import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchEventsByOrg, fetchEventByCode } from "../Api/ViewEvent";
import { Container, Table, Button, Modal, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";

import { getAllEventTypes } from "../Api/Event";
import { getDepartmentsByOrgId } from "../Api/DepartmentOrgMaster";
import { getAllCompanyCodes } from "../Api/CompanyLocationMaster";
import { fetchCostCenters } from "../Api/ResourceMaster";
import { getResourcesByOrgId } from "../Api/ResourceList";
import { fetchCustomersByOrgId } from "../Api/InvoiceApi";

const ViewEvent = () => {
  const navigate = useNavigate();
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const orgId = userDetails?.organisationId;

  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [eventTypeMap, setEventTypeMap] = useState({});

  // Maps for displaying names instead of codes
  const [departmentMap, setDepartmentMap] = useState({});
  const [companyMap, setCompanyMap] = useState({});
  const [costCenterMap, setCostCenterMap] = useState({});
  const [resourceMap, setResourceMap] = useState({});
  const [customerMap, setCustomerMap] = useState({});

  useEffect(() => {
    loadEvents();
    loadEventTypes();
    loadMaps();
  }, []);

  const loadEvents = async () => {
    try {
      const data = await fetchEventsByOrg(orgId);
      setEvents(data);
    } catch (error) {
      toast.error("Error fetching events");
    }
  };

  const handleView = async (eventCode) => {
    try {
      const data = await fetchEventByCode(orgId, eventCode);
      setSelectedEvent(data);
      setShowModal(true);
    } catch (error) {
      toast.error("Error fetching event details");
    }
  };

  const loadEventTypes = async () => {
    try {
      const data = await getAllEventTypes();
      const map = {};
      data.forEach((item) => {
        map[item.eventTypeGuid] = item.typeName;
      });
      setEventTypeMap(map);
    } catch {
      toast.error("Error loading event types");
    }
  };

  const loadMaps = async () => {
    try {
      // Load Departments
      const deptData = await getDepartmentsByOrgId(orgId);
      const deptMap = {};
      deptData.forEach((dept) => {
        deptMap[dept.guid] = dept.deptDescription;
      });
      setDepartmentMap(deptMap);

      // Load Companies
      const companyData = await getAllCompanyCodes(orgId);
      const companyMap = {};
      companyData.forEach((company) => {
        companyMap[company.companyCode] = company.name;
      });
      setCompanyMap(companyMap);

      // Load Cost Centers
      const costCenterData = await fetchCostCenters();
      const costCenterMap = {};
      costCenterData.forEach((center) => {
        costCenterMap[center.code] = center.description;
      });
      setCostCenterMap(costCenterMap);

      // Load Resources
      const resourceData = await getResourcesByOrgId(orgId);
      const resourceMap = {};
      resourceData.forEach((resource) => {
        resourceMap[resource.resourceId] = resource.name;
      });
      setResourceMap(resourceMap);

      // Load Customers
      const customerData = await fetchCustomersByOrgId(orgId);
      const customerMap = {};
      customerData.forEach((customer) => {
        customerMap[customer.customerCode] = customer.name;
      });
      setCustomerMap(customerMap);
    } catch (error) {
      console.error("Error loading maps:", error);
    }
  };

  const getDisplayValue = (value, map, defaultValue = "-") => {
    if (!value) return defaultValue;
    const displayName = map[value];
    return displayName ? `${displayName} (${value})` : value;
  };

  return (
    <div className="main-content expand">
      <Container fluid className="list-container">
        <h4 className="mb-3">Event List</h4>

        {events.length > 0 ? (
          <Table bordered hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>Event Code</th>
                <th>Event Name</th>
                <th>Type</th>
                <th>Date</th>
                <th>End Date</th>
                <th>Location</th>
                <th>Organizer</th>
                <th>Department</th>
                <th>Client</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {events.map((event, index) => (
                <tr key={event.eventCode}>
                  <td>{index + 1}</td>
                  <td>{event.eventCode}</td>
                  <td>{event.eventName}</td>
                  <td>{eventTypeMap[event.eventType] || event.eventType}</td>
                  <td>{moment(event.eventDate).format("DD-MM-YYYY")}</td>
                  <td>
                    {event.eventEndDate
                      ? moment(event.eventEndDate).format("DD-MM-YYYY")
                      : "-"}
                  </td>
                  <td>{event.eventLocation || "-"}</td>
                  <td>{event.organizerName || "-"}</td>
                  <td>{getDisplayValue(event.department, departmentMap)}</td>
                  <td>{getDisplayValue(event.eventClient, customerMap)}</td>
                  <td>{event.eventStatus}</td>
                  <td style={{ display: "flex" }}>
                    <Button
                      size="sm"
                      variant="info"
                      onClick={() => handleView(event.eventCode)}
                    >
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="warning"
                      className="ms-2"
                      onClick={() => navigate(`/editEvent/${event.eventCode}`)}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p>No events found</p>
        )}

        {/* EVENT DETAILS MODAL */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Event Details</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {selectedEvent && (
              <div>
                <Row>
                  <Col md={6}>
                    <p>
                      <strong>Event Code:</strong> {selectedEvent.eventCode}
                    </p>
                    <p>
                      <strong>Event Name:</strong> {selectedEvent.eventName}
                    </p>
                    <p>
                      <strong>Event Type:</strong>{" "}
                      {eventTypeMap[selectedEvent.eventType] ||
                        selectedEvent.eventType}
                    </p>
                    <p>
                      <strong>Event Date:</strong>{" "}
                      {moment(selectedEvent.eventDate).format("DD-MM-YYYY")}
                    </p>
                    <p>
                      <strong>End Date:</strong>{" "}
                      {selectedEvent.eventEndDate
                        ? moment(selectedEvent.eventEndDate).format(
                            "DD-MM-YYYY",
                          )
                        : "-"}
                    </p>
                    <p>
                      <strong>Start Time:</strong>{" "}
                      {selectedEvent.eventStartTime
                        ? selectedEvent.eventStartTime.slice(0, 5)
                        : "-"}
                    </p>
                    <p>
                      <strong>End Time:</strong>{" "}
                      {selectedEvent.eventEndTime
                        ? selectedEvent.eventEndTime.slice(0, 5)
                        : "-"}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p>
                      <strong>Location:</strong>{" "}
                      {selectedEvent.eventLocation || "-"}
                    </p>
                    <p>
                      <strong>Organizer:</strong>{" "}
                      {selectedEvent.organizerName || "-"}
                    </p>
                    <p>
                      <strong>Budget:</strong>{" "}
                      {selectedEvent.budgetAllocation || "-"}
                    </p>
                    <p>
                      <strong>Department:</strong>{" "}
                      {getDisplayValue(selectedEvent.department, departmentMap)}
                    </p>
                    <p>
                      <strong>Company Code:</strong>{" "}
                      {getDisplayValue(
                        selectedEvent.eventCompanyCode,
                        companyMap,
                      )}
                    </p>
                    <p>
                      <strong>Cost Center:</strong>{" "}
                      {getDisplayValue(
                        selectedEvent.eventCostCenter,
                        costCenterMap,
                      )}
                    </p>
                    <p>
                      <strong>Resource:</strong>{" "}
                      {getDisplayValue(
                        selectedEvent.eventResource,
                        resourceMap,
                      )}
                    </p>
                    <p>
                      <strong>Event Client:</strong>{" "}
                      {getDisplayValue(selectedEvent.eventClient, customerMap)}
                    </p>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <p>
                      <strong>Description:</strong>
                    </p>
                    <p>{selectedEvent.eventDescription || "-"}</p>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <p>
                      <strong>Entity Type:</strong>{" "}
                      {selectedEvent.entityType || "-"}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p>
                      <strong>Entity ID:</strong>{" "}
                      {selectedEvent.entityId || "-"}
                    </p>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <p>
                      <strong>Approval Status:</strong>{" "}
                      {selectedEvent.approvalStatus}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p>
                      <strong>Event Status:</strong> {selectedEvent.eventStatus}
                    </p>
                  </Col>
                </Row>
              </div>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
            <Button
              variant="warning"
              onClick={() => {
                setShowModal(false);
                navigate(`/editEvent/${selectedEvent?.eventCode}`);
              }}
            >
              Edit Event
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default ViewEvent;
